/**
 * Global WebSocket Composable
 * Manages WebSocket connection for real-time updates across the application
 */

import { ref, computed } from 'vue'
import { API_CONFIG, FEATURE_FLAGS } from '../config'
import { endpoints } from '../services/api/base'
import type { WebSocketMessage } from '../types/websocket'
import { WebSocketErrorCode } from '../types/websocket'

interface GlobalWebSocketState {
  ws: WebSocket | null
  isConnected: boolean
  isConnecting: boolean
  reconnectAttempts: number
  lastError: string | null
  connectionId: string | null
  organizationId: number | null
  heartbeatTimer: number | null
  authToken: string | null
}

// Global state
const state = ref<GlobalWebSocketState>({
  ws: null,
  isConnected: false,
  isConnecting: false,
  reconnectAttempts: 0,
  lastError: null,
  connectionId: null,
  organizationId: null,
  heartbeatTimer: null,
  authToken: null,
})

// Message handlers registry - support multiple handlers per message type
const messageHandlers = new Map<string, Set<(message: WebSocketMessage) => void>>()

// Configuration
const config = {
  maxReconnectAttempts: 5,
  reconnectDelayMs: 1000,
  heartbeatIntervalMs: 30000,
  connectionTimeoutMs: 10000,
}

export function useGlobalWebSocket() {
  // Computed properties
  const isConnected = computed(() => state.value.isConnected)
  const isConnecting = computed(() => state.value.isConnecting)
  const lastError = computed(() => state.value.lastError)
  const connectionId = computed(() => state.value.connectionId)
  const organizationId = computed(() => state.value.organizationId)

  // Connect to WebSocket
  const connect = async (orgId: number, token: string) => {
    if (!FEATURE_FLAGS.WEBSOCKET_ENABLED) {
      console.warn('WebSocket is disabled via feature flag')
      return false
    }

    if (state.value.isConnecting || state.value.isConnected) {
      console.warn('WebSocket connection already in progress or connected')
      return false
    }

    try {
      state.value.isConnecting = true
      state.value.lastError = null
      state.value.organizationId = orgId
      state.value.authToken = token

      const getWsBaseUrl = () => {
        if (API_CONFIG.WS_BASE_URL) return API_CONFIG.WS_BASE_URL

        // Fallback: derive from current window location if possible, or use defaults
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const host = window.location.host || 'localhost:8300'
        return `${protocol}//${host}`
      }

      const wsUrl = `${getWsBaseUrl()}${endpoints.websocket.syncStatus(orgId)}?token=${token}`
      // console.log('🔌 Connecting to WebSocket:', wsUrl)

      const ws = new WebSocket(wsUrl)
      state.value.ws = ws

      // Set up event handlers
      ws.onopen = handleOpen
      ws.onmessage = handleMessage
      ws.onclose = handleClose
      ws.onerror = handleError

      // Set connection timeout
      const timeoutId = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.error('WebSocket connection timeout')
          ws.close()
        }
      }, config.connectionTimeoutMs)

      // Store timeout ID for cleanup
      ws.addEventListener('open', () => clearTimeout(timeoutId))
      ws.addEventListener('close', () => clearTimeout(timeoutId))
      ws.addEventListener('error', () => clearTimeout(timeoutId))

      return true
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      state.value.lastError = error instanceof Error ? error.message : 'Unknown error'
      state.value.isConnecting = false
      return false
    }
  }

  // Disconnect from WebSocket
  const disconnect = () => {
    // console.log('🔌 Disconnecting from WebSocket')

    // Clear heartbeat timer
    if (state.value.heartbeatTimer) {
      clearInterval(state.value.heartbeatTimer)
      state.value.heartbeatTimer = null
    }

    // Close WebSocket connection
    if (state.value.ws) {
      state.value.ws.close(1000, 'Client disconnect')
      state.value.ws = null
    }

    // Reset state
    state.value.isConnected = false
    state.value.isConnecting = false
    state.value.connectionId = null
    state.value.organizationId = null
    state.value.reconnectAttempts = 0
  }

  // Handle WebSocket open
  const handleOpen = (_event: Event) => {
    // console.log('✅ WebSocket connected')
    state.value.isConnected = true
    state.value.isConnecting = false
    state.value.reconnectAttempts = 0
    state.value.lastError = null
    // organizationId/token уже сохранены при connect()

    // Generate connection ID
    state.value.connectionId = `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Start heartbeat
    startHeartbeat()

    // Notify handlers
    notifyHandlers('connection_status', {
      type: 'connection_status',
      timestamp: Date.now(),
      data: {
        status: 'connected',
        organization_id: state.value.organizationId,
        connection_id: state.value.connectionId,
        connected_at: new Date().toISOString(),
      },
    })
  }

  // Handle WebSocket message
  const handleMessage = (event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)
      // incoming message

      // Notify all registered handlers (including heartbeat and pong)
      notifyHandlers(message.type, message)
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  // Handle WebSocket close
  const handleClose = (event: CloseEvent) => {
    // disconnected

    state.value.isConnected = false
    state.value.isConnecting = false
    state.value.ws = null

    // Clear heartbeat timer
    if (state.value.heartbeatTimer) {
      clearInterval(state.value.heartbeatTimer)
      state.value.heartbeatTimer = null
    }

    // Handle reconnection based on close code
    if (shouldReconnect(event.code)) {
      scheduleReconnect()
    } else {
      state.value.lastError = `Connection closed: ${event.code} - ${event.reason}`
    }

    // Notify handlers
    notifyHandlers('connection_status', {
      type: 'connection_status',
      timestamp: Date.now(),
      data: {
        status: 'disconnected',
        organization_id: state.value.organizationId,
        connection_id: state.value.connectionId,
        disconnected_at: new Date().toISOString(),
        close_code: event.code,
        close_reason: event.reason,
      },
    })
  }

  // Handle WebSocket error
  const handleError = (event: Event) => {
    console.error('❌ WebSocket error:', event)
    state.value.lastError = 'WebSocket connection error'
    state.value.isConnecting = false
  }

  // Start heartbeat
  const startHeartbeat = () => {
    if (state.value.heartbeatTimer) {
      clearInterval(state.value.heartbeatTimer)
    }

    state.value.heartbeatTimer = window.setInterval(() => {
      if (state.value.ws?.readyState === WebSocket.OPEN) {
        sendPing()
      }
    }, config.heartbeatIntervalMs)
  }

  // Send ping message
  const sendPing = () => {
    if (state.value.ws?.readyState === WebSocket.OPEN) {
      const pingMessage = {
        type: 'ping',
        timestamp: Date.now(),
      }
      state.value.ws.send(JSON.stringify(pingMessage))
    }
  }

  // Schedule reconnection
  const scheduleReconnect = () => {
    if (state.value.reconnectAttempts >= config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      state.value.lastError = 'Max reconnection attempts reached'
      return
    }

    state.value.reconnectAttempts++
    const delay = Math.min(30000, config.reconnectDelayMs * Math.pow(2, state.value.reconnectAttempts - 1))

    // schedule reconnect with backoff

    setTimeout(() => {
      const orgId = state.value.organizationId
      const token = state.value.authToken
      if (orgId && token) {
        // console.log('🔄 Attempting WebSocket reconnect with stored credentials')
        void connect(orgId, token)
      } else {
        console.warn('Reconnection skipped: missing orgId or token')
      }
    }, delay)
  }

  // Check if should reconnect based on close code
  const shouldReconnect = (code: number): boolean => {
    // Don't reconnect for normal closure or policy violation
    if (code === WebSocketErrorCode.NORMAL_CLOSURE || code === WebSocketErrorCode.POLICY_VIOLATION) {
      return false
    }
    return true
  }

  // Register message handler
  const onMessage = (messageType: string, handler: (message: WebSocketMessage) => void) => {
    // Get or create handlers set for this message type
    if (!messageHandlers.has(messageType)) {
      messageHandlers.set(messageType, new Set())
    }

    const handlers = messageHandlers.get(messageType)!
    handlers.add(handler)

    // console.log(`🔌 [WebSocket] Registered handler for ${messageType} (total: ${handlers.size})`)

    // Return unsubscribe function
    return () => {
      handlers.delete(handler)
      // console.log(`🔌 [WebSocket] Unregistered handler for ${messageType} (remaining: ${handlers.size})`)
      // Clean up empty sets
      if (handlers.size === 0) {
        messageHandlers.delete(messageType)
      }
    }
  }

  // Notify all handlers
  const notifyHandlers = (messageType: string, message: WebSocketMessage) => {
    const handlers = messageHandlers.get(messageType)
    if (handlers) {
      // console.log(`📨 [WebSocket] Notifying ${handlers.size} handlers for ${messageType}`)
      handlers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          console.error(`❌ [WebSocket] Error in message handler for ${messageType}:`, error)
        }
      })
    } else {
      // console.log(`📨 [WebSocket] No handlers registered for ${messageType}`)
    }
  }

  // Send message to WebSocket
  const sendMessage = (message: any) => {
    if (state.value.ws?.readyState === WebSocket.OPEN) {
      // console.log('📤 [WebSocket] Sending message:', message)
      state.value.ws.send(JSON.stringify(message))
      return true
    } else {
      console.warn('📤 [WebSocket] Cannot send message - WebSocket not open. State:', state.value.ws?.readyState)
      return false
    }
  }

  // Get connection status
  const getConnectionStatus = () => {
    return {
      isConnected: state.value.isConnected,
      isConnecting: state.value.isConnecting,
      lastError: state.value.lastError,
      connectionId: state.value.connectionId,
      organizationId: state.value.organizationId,
      reconnectAttempts: state.value.reconnectAttempts,
    }
  }

  // Cleanup function (call manually when needed)
  const cleanup = () => {
    disconnect()
  }

  return {
    // State
    isConnected,
    isConnecting,
    lastError,
    connectionId,
    organizationId,

    // Methods
    connect,
    disconnect,
    onMessage,
    // No-op default handler to avoid noisy warnings when nobody subscribed
    // Example usage: globalWebSocket.onMessage('connection_status', () => {})
    sendMessage,
    getConnectionStatus,
    cleanup,
  }
}

// Export singleton instance
export const globalWebSocket = useGlobalWebSocket()
